import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  effect,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import {
  ActivatedRoute,
  Router,
  RouterModule,
} from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import {
  MatBottomSheet,
  MatBottomSheetModule,
} from "@angular/material/bottom-sheet";
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import {
  LucideAngularModule,
  MessageSquare,
  BookOpen,
  ArrowLeft,
  BookUser,
  Settings,
  Eraser,
  ClipboardCopy,
  Upload,
} from "lucide-angular";
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChatEventService } from "../../../core/services/chat-event.service";
import {
  ChatbotSettingsBottomSheetComponent,
  SettingsData,
} from "./components/settings-bottom-sheet.component";
import { copySessionIdToClipboard, getConnectionStatusColor } from "../../../core/utils/chat-ui.util";
import { getBotGradientStyleUtil, copyFormattedChatHistory } from "../../../core/utils/chat-component.util";
import { RecentItemsService } from "../../../core/services/recent-items.service";
import { ChatHeaderComponent } from "../../../shared/components/chat/chat-header/chat-header.component";
import { ChatInfoBarComponent } from "../../../shared/components/chat/chat-info-bar/chat-info-bar.component";
import { BotNotFoundComponent } from "../../../shared/components/chat/bot-not-found/bot-not-found.component";
import { MessageInputComponent } from "../../../shared/components/chat/message-input/message-input.component";
import { TypingIndicatorComponent } from "../../../shared/components/chat/typing-indicator/typing-indicator.component";
import { MessageListComponent as NewMessageListComponent } from "./components/message-list.component";
import { Subject } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { ChatbotInteractionService } from '../../chatbot-interaction/services/chatbot-interaction.service';
import { Chatbot } from '../../../core/models/chatbot.model';
import { SessionBenchmarkDialogComponent } from '../../conversation-viewer/components/session-benchmark-dialog.component';

@Component({
  selector: "app-chatbot-chat-page",
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatButtonModule,
    MatSnackBarModule,
    MatBottomSheetModule,
    MatDialogModule,
    LucideAngularModule,
    MatTooltipModule,
    ChatHeaderComponent,
    ChatInfoBarComponent,
    BotNotFoundComponent,
    MessageInputComponent,
    TypingIndicatorComponent,
    NewMessageListComponent
],
  templateUrl: "./chat-page.component.html",
  styleUrls: ["./chat-page.component.scss"],
})
export class ChatbotChatPageComponent implements OnInit, OnDestroy {
  @ViewChild("messagesContainer") messagesContainer!: ElementRef;

  readonly chatEventService = inject(ChatEventService);
  readonly chatbotService = inject(ChatbotInteractionService);
  readonly recentItemsService = inject(RecentItemsService);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly snackBar = inject(MatSnackBar);
  readonly bottomSheet = inject(MatBottomSheet);
  readonly dialog = inject(MatDialog);

  // Convert observables to signals for template binding
  messages = toSignal(this.chatEventService.messages$, { initialValue: [] });
  isTyping = toSignal(this.chatEventService.isTyping$, { initialValue: false });
  connected = toSignal(this.chatEventService.connected$, { initialValue: false });
  connection = toSignal(this.chatEventService.connection$, {
    initialValue: "DISCONNECTED",
  });
  sessionId = toSignal(this.chatEventService.sessionId$);

  message = "";
  whatsappNumber = "";
  tempWhatsappNumber = "";
  currentBot: Chatbot | null = null;
  loading = true;

  // Icons
  readonly MessageSquare = MessageSquare;
  readonly BookOpen = BookOpen;
  readonly ArrowLeft = ArrowLeft;
  readonly BookUser = BookUser;
  readonly Settings = Settings;
  readonly Eraser = Eraser;
  readonly ClipboardCopy = ClipboardCopy;
  readonly Upload = Upload;

  private isSettingsOpen = false;
  readonly destroy$ = new Subject<void>();

  constructor() {
    this.setupNavigationCleanup();

    // Autoscroll for any message
    effect(() => {
      this.messages();
      setTimeout(() => {
        this.scrollToBottom();
      }, 0);
    });
  }

  ngOnDestroy(): void {
    this.message = "";
    this.destroy$.next();
    this.destroy$.complete();
    // Note: WebSocket disconnection is handled by ChatEventService lifecycle
  }

  ngOnInit(): void {
    const botCode = this.route.snapshot.params["code"];
    
    if (!botCode) {
      this.router.navigate(["/chatbot/chats"]);
      return;
    }

    // Load bot details from backend
    this.loadBotDetails(botCode);
    
    // Load saved settings
    this.loadSavedSettings();
  }

  /**
   * Loads bot details from the backend
   */
  private loadBotDetails(code: string): void {
    this.loading = true;
    
    this.chatbotService.getChatbotsByUser().subscribe({
      next: (chatbots) => {
        // Find bot by code
        this.currentBot = chatbots.find(bot => bot.code === code) || null;
        
        if (!this.currentBot) {
          this.snackBar.open("Chatbot no encontrado", "Cerrar", {
            duration: 5000,
          });
          this.router.navigate(["/chatbot/chats"]);
          return;
        }

        this.recentItemsService.addRecentChatbot(
          this.currentBot.code,
          this.currentBot.name
        );

        // Initialize chat service with bot code and endpoint
        this.chatEventService.switchBot(
          this.currentBot.code,
          this.currentBot.endpointToSend
        );
        this.loading = false;

        // Open settings if no WhatsApp number
        if (!this.whatsappNumber) {
          setTimeout(() => {
            this.openSettings();
          }, 500);
        }
      },
      error: (error) => {
        this.snackBar.open("Error al cargar el chatbot", "Cerrar", {
          duration: 5000,
        });
        this.router.navigate(["/chatbot/chats"]);
      },
    });
  }

  /**
   * Loads saved settings from local storage
   */
  private loadSavedSettings(): void {
    try {
      const savedSettings = localStorage.getItem("chatSettings");
      if (savedSettings) {
        const { whatsappNumber } = JSON.parse(savedSettings);
        if (whatsappNumber) {
          this.whatsappNumber = whatsappNumber;
          this.tempWhatsappNumber = whatsappNumber;
        }
      }
    } catch (error) {
      console.error('Error loading saved settings:', error);
    }
  }

  sendMessage(messageText?: string): void {
    const textToSend = messageText || this.message;
    
    if (!textToSend.trim()) return;

    // Validate WhatsApp number
    if (!this.whatsappNumber) {
      this.snackBar.open(
        "Por favor, ingresa tu número de WhatsApp antes de enviar un mensaje.",
        "Cerrar",
        {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    // Send the message through the chat service
    this.chatEventService.sendMessage(textToSend, this.whatsappNumber);
    
    // Only clear the input if we're sending from the text input
    if (!messageText) {
      this.message = "";
    }
  }

  /**
   * Handles messages sent from buttons or select lists in MessageListComponent
   */
  onSendMessage(message: string): void {
    // Send the message
    this.sendMessage(message);
  }

  startNewSession() {
    this.chatEventService.startNewSession();
    this.snackBar.open('Nueva sesión iniciada', 'Cerrar', {
      duration: 2000,
    });
  }

  public copySessionId(): void {
    copySessionIdToClipboard(this.sessionId(), (msg) => {
      if (msg.includes('copiado')) {
        this.snackBar.open(`✅ ${msg}`, "", {
          duration: 3000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["success-snackbar"],
        });
      } else {
        this.snackBar.open(msg, "Cerrar", {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        });
      }
    });
  }

  goBackToBots() {
    this.router.navigate(["/chatbot/chats"]);
  }

  public scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const container = this.messagesContainer.nativeElement;
        container.scrollTop = container.scrollHeight;
      }
    } catch (err) {
      console.error(err)
    }
  }

  onMessageInputKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onQuickReplySelected(reply: string): void {
    this.message = reply;
    this.sendMessage();
  }

  validateWhatsAppNumber(): void {
    const phoneRegex = /^\+[1-9]\d{1,14}$/;

    if (!this.tempWhatsappNumber.trim()) {
      this.snackBar.open(
        "Por favor, ingresa tu número de WhatsApp.",
        "Cerrar",
        {
          duration: 4000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["warning-snackbar"],
        },
      );
      return;
    }

    if (!phoneRegex.test(this.tempWhatsappNumber.trim())) {
      this.snackBar.open(
        "Por favor, ingresa un número de WhatsApp válido con código de país (ej: +56912345678).",
        "Cerrar",
        {
          duration: 6000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    this.whatsappNumber = this.tempWhatsappNumber.trim();
    this.snackBar.open("✅ Número de WhatsApp confirmado correctamente", "", {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: ["success-snackbar"],
    });
  }

  clearWhatsAppNumber(): void {
    this.whatsappNumber = "";
    this.tempWhatsappNumber = "";
  }

  getStatusColor(): string {
    return getConnectionStatusColor(this.connected(), this.currentBot?.isActive);
  }

  getBotGradientStyle(): any {
    return getBotGradientStyleUtil(this.currentBot || undefined);
  }

  private setupNavigationCleanup(): void {
    // No need to listen to navigation events here
    // Cleanup will be handled in ngOnDestroy when component is actually destroyed
  }

  private cleanupBeforeNavigation(): void {
    this.message = "";
    // WebSocket cleanup is handled in ChatEventService.ngOnDestroy
  }

  openSettings(): void {
    if (this.isSettingsOpen) {
      return;
    }

    this.isSettingsOpen = true;
    const dialogRef = this.bottomSheet.open(ChatbotSettingsBottomSheetComponent, {
      data: {
        whatsappNumber: this.whatsappNumber,
      } as SettingsData,
    });

    dialogRef.afterDismissed().subscribe((result?: SettingsData) => {
      this.isSettingsOpen = false;

      if (!result) {
        return;
      }

      // Save settings to local storage
      localStorage.setItem(
        "chatSettings",
        JSON.stringify({
          whatsappNumber: result.whatsappNumber,
        }),
      );

      // Update WhatsApp number if changed
      if (result.whatsappNumber !== this.whatsappNumber) {
        this.whatsappNumber = result.whatsappNumber;
        this.tempWhatsappNumber = result.whatsappNumber;
      }
    });
  }

  getEnvironmentName(env: string): string {
    return "Producción";
  }

  async copyChatToClipboard(): Promise<void> {
    const whatsappNumber = this.whatsappNumber || "No definido";

    await copyFormattedChatHistory(
      this.messages(),
      this.sessionId(),
      {
        'Número': whatsappNumber,
        'Bot': this.currentBot?.name || 'N/A'
      },
      this.snackBar
    );
  }

  openLiveBenchmark(): void {
    const sessionId = this.sessionId();
    const allMessages = this.messages();

    if (!sessionId || allMessages.length === 0) {
      this.snackBar.open('No hay datos suficientes para generar el benchmark', 'Cerrar', {
        duration: 3000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.dialog.open(SessionBenchmarkDialogComponent, {
      data: {
        sessionId,
        conversationId: sessionId,
        allMessages,
        liveProcessTraces: this.chatEventService.pendingProcessMessages()
      },
      width: '96vw',
      maxWidth: '96vw',
      maxHeight: '92vh',
      panelClass: 'session-benchmark-dialog',
      autoFocus: false
    });
  }
}

